'use client';

import {CdnImageSizes} from '@config/cdnImageSizes';
import {getEnv} from '@config/getEnv';
import classNames from 'classnames';
import {
    ComponentProps,
    FC,
    SyntheticEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import styles from './MeinImage.module.css';
import {generateCdnImageSrc} from './generateCdnImageSrc';

export interface IMeinImageProps extends ComponentProps<'img'> {
    withFade?: boolean;
    src2x?: string;
    size?: keyof typeof CdnImageSizes;
    focusPosition?: {x?: number | null; y?: number | null};
}

export const MeinImage: FC<IMeinImageProps> = ({
    alt,
    withFade,
    loading = 'lazy',
    className,
    onLoad,
    src,
    src2x,
    width,
    size,
    focusPosition,
    height,
    ...rest
}) => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
        setHasLoaded(true);
        onLoad?.(e);
    };

    const cdnImageUrl = getEnv('NEXT_PUBLIC_HOST_CDN_IMAGE') || 'cdn.wehorse';

    const isScalableCDNImage =
        typeof src === 'string' &&
        src.toLowerCase().includes(cdnImageUrl.toLowerCase()) &&
        !src.toLowerCase().endsWith('.svg');

    const imgProps = (() => {
        if (isScalableCDNImage) {
            const sizes =
                size && size in CdnImageSizes ? CdnImageSizes[size] : [0, 0];
            return {
                width: width ?? sizes[0],
                height: height ?? sizes[1],
                ...generateCdnImageSrc(src, {
                    size,
                    focusPositionX: focusPosition?.x,
                    focusPositionY: focusPosition?.y,
                }),
            };
        }
        return {
            width,
            height,
            src: src,
            src2x,
            srcSet: src2x ? `${src2x} 2x` : undefined,
        };
    })();

    useEffect(() => {
        if (hasLoaded) {
            return;
        }

        if (imgRef.current?.complete) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setHasLoaded(true);
            onLoad?.({
                currentTarget: imgRef.current,
                target: imgRef.current,
            } as unknown as SyntheticEvent<HTMLImageElement>);
        }
    }, [hasLoaded, onLoad]);

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            ref={imgRef}
            onLoad={handleLoad}
            className={classNames(
                {
                    [styles.image]: withFade,
                    [styles.imageFadeIn]: withFade && hasLoaded,
                },
                className,
            )}
            alt={alt ?? ''}
            loading={loading}
            draggable="false"
            {...imgProps}
            {...rest}
        />
    );
};
